import gql from 'graphql-tag';
import client from '../../../api/graphql/client';

/**
 * GraphQL query to fetch user data with additional fields for token processing
 */
const GET_USERS_WITH_DETAILS = gql`
  query GetUsersWithDetails {
    users {
      _id
      firstName
      lastName
      emails {
        address
      }
      department
      title
      role
      payrollId
      employeeType
      defaultRate
      supervisor {
        _id
        firstName
        lastName
        emails {
          address
        }
      }
    }
  }
`;

/**
 * Extract user data from rows and fetch additional details
 * @param {Array} rows - Array of row data containing user emails
 * @returns {Promise<Map>} Map of email to user data
 */
export const extractAndEnrichUserData = async (rows) => {
  // Extract unique emails from rows
  const uniqueEmails = new Set();
  rows.forEach(row => {
    if (row.email) {
      uniqueEmails.add(row.email);
    }
  });
  
  const emailsArray = Array.from(uniqueEmails);
  if (emailsArray.length === 0) {
    return new Map();
  }
  
  try {
    // Fetch all users from GraphQL
    const { data } = await client.query({
      query: GET_USERS_WITH_DETAILS,
      fetchPolicy: 'network-only' // Always fetch fresh data
    });
    
    // Create a map of email to user data
    const userMap = new Map();
    
    if (data?.users) {
      // Filter users to only include those we need
      data.users.forEach(user => {
        // Use the first email address as the key
        const userEmail = user.emails?.[0]?.address;
        if (userEmail && emailsArray.includes(userEmail)) {
          userMap.set(userEmail, {
            ...user,
            email: userEmail, // Ensure email is available at top level
            supervisorName: user.supervisor 
              ? `${user.supervisor.firstName} ${user.supervisor.lastName}`
              : undefined,
            supervisorEmail: user.supervisor?.emails?.[0]?.address
          });
        }
      });
    }
    
    return userMap;
  } catch (error) {
    console.error('Error fetching user details:', error);
    return new Map();
  }
};

/**
 * Create a user data object from row data and enriched user map
 * @param {Object} row - Row data
 * @param {Map} userMap - Map of email to enriched user data
 * @returns {Object} User data object for token processing
 */
export const getUserDataForRow = (row, userMap) => {
  if (!row.email || !userMap.has(row.email)) {
    // Return basic data from row if no enriched data available
    return {
      firstName: row.firstName,
      lastName: row.lastName,
      email: row.email,
      role: row.role,
      payrollId: row.payrollId,
      name: row.name,
      supervisor: row.supervisor
    };
  }
  
  const enrichedUser = userMap.get(row.email);
  
  // Merge row data with enriched data (row data takes precedence for consistency)
  return {
    ...enrichedUser,
    firstName: row.firstName || enrichedUser.firstName,
    lastName: row.lastName || enrichedUser.lastName,
    email: row.email || enrichedUser.email,
    role: row.role || enrichedUser.role,
    payrollId: row.payrollId || enrichedUser.payrollId,
    name: row.name || `${enrichedUser.firstName} ${enrichedUser.lastName}`,
    // Additional enriched fields
    department: enrichedUser.department,
    title: enrichedUser.title,
    employeeType: enrichedUser.employeeType,
    defaultRate: enrichedUser.defaultRate,
    supervisor: enrichedUser.supervisor,
    supervisorName: enrichedUser.supervisorName || row.supervisor
  };
};

/**
 * Get all available token fields from row data and enriched user data
 * @param {Object} sampleRow - A sample row to extract fields from
 * @param {Map} userMap - Map of enriched user data
 * @returns {Array<string>} Array of available token fields
 */
export const getAvailableTokenFields = (sampleRow, userMap) => {
  const fields = new Set();
  
  // Add all fields from the row
  if (sampleRow) {
    Object.keys(sampleRow).forEach(key => {
      if (typeof sampleRow[key] !== 'object') {
        fields.add(key);
      }
    });
  }
  
  // Add enriched user fields if available
  if (userMap && userMap.size > 0) {
    // Get a sample user to see available fields
    const sampleUser = userMap.values().next().value;
    if (sampleUser) {
      // Add direct user fields
      ['department', 'title', 'employeeType', 'supervisorName'].forEach(field => {
        if (sampleUser[field] !== undefined) {
          fields.add(field);
        }
      });
    }
  }
  
  // Convert to sorted array
  return Array.from(fields).sort();
};